/**
 * Main Menu — the TV "CHANNEL GUIDE" (design 05 · UI — Main Menu, A8KMkP): channel rows on the left (CH number,
 * show title, one-line description, tag), a preview monitor on the right showing tonight's matchup for the
 * focused channel with an ON AIR bug, a mode info panel (ranked: the tier ladder), button hints and the HFN
 * SCORES ticker.
 */
import { storeReturn } from './store-nav';
import { signal } from '@preact/signals';
import { useMemo } from 'preact/hooks';
import { CHARACTERS, type CharacterId } from '@hf/shared';
import { useActions, useServices } from '../actions';
import { Art, Glove, Head, Rays } from '../components';
import { lobbyTab, updateDraft } from '@hf/client-core/ui-model/flow';
import { RANK_TIERS, rankTierIndex } from '@hf/client-core/ui-model/format';
import { useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import type { ScreenId } from '@hf/client-core/ui-model/types';
import { Shell } from './Shell';
import { PORTAL } from '../../portal';
import { openHowToPlay } from '../tutorial/HowToPlay';
import { NewHereOffer } from '../tutorial/NewHere';
import { doneCount, graduated, newHereEligible, progress } from '@hf/client-core/ui-model/tutorial/progress';
import { guideTime } from '@hf/client-core/ui-model/screens';
export { guideTime } from '@hf/client-core/ui-model/screens';

interface Channel {
  id: string;
  title: string;
  desc: string;
  tag: string;
  info: string;
  heads: [CharacterId, CharacterId];
  rays: [string, string];
  /**
   * What the ON AIR monitor shows: a VS fight card for the match channels, else the channel's own picture (the
   * coach at his chalkboard, the counter square, a drill with cones, the settings desk).
   */
  scene?: 'vs' | 'coach' | 'rules' | 'drill' | 'settings';
  go(): ScreenId;
  /** Opens an overlay instead of navigating (HOW TO PLAY). */
  open?(): void;
}

const ALL_CHANNELS: readonly Channel[] = [
  {
    id: 'exhibition',
    title: 'EXHIBITION',
    desc: 'Quick game vs the CPU · four difficulties',
    tag: '1P',
    info: 'Pick a head, pick a CPU and play a full drives game. Harder CPUs read the counter square better.',
    heads: ['rookie', 'bigtex'],
    rays: ['#e8343c', '#7a1430'],
    go: () => (updateDraft({ mode: 'cpu' }), 'mode_select'),
  },
  {
    id: 'versus',
    title: 'VERSUS',
    desc: 'Local couch 1v1 · shared keyboard or two pads',
    tag: '2P',
    info: 'Same screen, two players. P1 plays WASD + J/K, P2 plays the arrows + 1/2 on the numpad (or . and /).',
    heads: ['zorg', 'grandpa'],
    rays: ['#2f7df6', '#14306e'],
    go: () => (updateDraft({ mode: 'local' }), 'mode_select'),
  },
  {
    id: 'online',
    title: 'ONLINE',
    desc: 'Quick match & private rooms · rollback netcode',
    tag: 'NET',
    info: 'Find a quick match or open a private room and share the 4-letter code with a friend.',
    heads: ['bigtex', 'zorg'],
    rays: ['#4ee6ff', '#155a7a'],
    go: () => ((lobbyTab.value = 'quick'), 'online_lobby'),
  },
  {
    id: 'ranked',
    title: 'RANKED',
    desc: 'Drive-based fair play · Rookie → Hall of Fame',
    tag: 'ELO',
    info: 'Equal drives, same yard line, normalized heads, no specials, no dice. Climb the ladder:',
    heads: ['grandpa', 'rookie'],
    rays: ['#ff2e88', '#5a1240'],
    go: () => ((lobbyTab.value = 'ranked'), 'online_lobby'),
  },
  {
    id: 'tutorial',
    scene: 'coach',
    title: 'FOOTBALL 101',
    desc: 'Tutorial · learn the game one rule at a time',
    tag: 'NEW',
    info: 'Never watched football? Four downs, ten yards, one glove. Ten minutes and you are calling fakes.',
    heads: ['rookie', 'grandpa'],
    rays: ['#ffd23f', '#7a5200'],
    go: () => 'tutorial',
  },
  {
    id: 'howto',
    scene: 'rules',
    title: 'HOW TO PLAY',
    desc: 'The rulebook · controls · counter square · fakes',
    tag: 'RULES',
    info: 'Football in eight ideas, every control on your own keys or pad, the counter square, fakes, meters, specials and scoring.',
    heads: ['grandpa', 'rookie'],
    rays: ['#ff8a1f', '#5a2a00'],
    go: () => 'main_menu',
    open: () => openHowToPlay(),
  },
  {
    id: 'practice',
    scene: 'drill',
    title: 'PRACTICE',
    desc: 'Infinite downs · drill fakes and the counter square',
    tag: '1P',
    info: 'No clock, no score. Reset the ball any time and switch between offense and defense.',
    heads: ['zorg', 'bigtex'],
    rays: ['#b6ff3b', '#2f5a10'],
    go: () => 'practice',
  },
  {
    id: 'store',
    title: 'STORE',
    desc: 'Heads · ball skins · your locker',
    tag: 'COINS',
    info: 'Spend the coins you earn in matches on new heads and ball skins, then equip them from your locker. Cosmetic only in ranked.',
    heads: ['bigtex', 'rookie'],
    rays: ['#ffd23f', '#7a3d00'],
    go: () => ((storeReturn.value = 'main_menu'), 'store'),
  },
  {
    id: 'options',
    scene: 'settings',
    title: 'OPTIONS',
    desc: PORTAL ? 'Audio · controls · CRT filter' : 'Sign in · audio · controls · CRT filter',
    tag: 'SET',
    info: 'Rebind every button on keyboard or pad, tune the mix, turn on the CRT filter or reduce motion.',
    heads: ['grandpa', 'zorg'],
    rays: ['#a77bff', '#2c1760'],
    go: () => 'settings',
  },
];

/** Portal build: vs CPU + local couch 2P only (no online, ranked or store). */
const PORTAL_HIDDEN = new Set(['online', 'ranked', 'store']);
export const CHANNELS: readonly Channel[] = PORTAL
  ? ALL_CHANNELS.filter((c) => !PORTAL_HIDDEN.has(c.id))
  : ALL_CHANNELS;

/** FOOTBALL 101's tag: NEW, then lessons done (n/12), then GRAD. */
const tutorialTag = (): string => {
  const p = progress.value;
  if (graduated(p)) return 'GRAD';
  const n = doneCount(p);
  return n === 0 ? 'NEW' : `${n}/12`;
};

/** Focus memory: returning to the guide lands on the channel you left from. */
const lastChannel = signal(0);

/** Point the guide's focus memory at a channel (e.g. FOOTBALL 101 when a lesson starts from anywhere). */
export const rememberChannel = (id: string): void => {
  const i = CHANNELS.findIndex((c) => c.id === id);
  if (i >= 0) lastChannel.value = i;
};

const TICKER = [
  <span>
    <b>ROK 21</b> ZRG 17 <b>FINAL</b>
  </span>,
  <span>
    <b>TEX 14</b> GRT 10 · 4TH & 2
  </span>,
  <span>ZORG'S MIRAGE FOOLS 3 DEFENDERS</span>,
  <span>GRANDPA GRIT: "BACK IN MY DAY WE BLITZED UPHILL"</span>,
  <span>
    TIP · <b>HURDLE</b> BEATS DIVE · <b>LEAP</b> BEATS HURDLE · <b>TRUCK</b> BEATS LEAP · <b>DIVE</b> BEATS
    TRUCK
  </span>,
  <span>
    TIP · SELL THE JUMP, PRESS <b>DOWN</b> WHILE RISING — WHAT A FAKE!
  </span>,
];

export function MainMenu() {
  const actions = useActions();
  const { audio } = useServices();
  const grid = useFocusGrid({
    count: CHANNELS.length,
    initial: lastChannel.peek(),
    onMove: (i) => {
      lastChannel.value = i;
      playSfx(audio, UI_SFX.move);
    },
    onConfirm: (i) => {
      lastChannel.value = i;
      playSfx(audio, UI_SFX.confirm);
      const c = CHANNELS[i]!;
      if (c.open) c.open();
      else actions.goto(c.go());
    },
    onBack: () => {
      playSfx(audio, UI_SFX.back);
      actions.goto('title');
    },
  });
  const ch = CHANNELS[grid.index]!;
  const time = useMemo(() => guideTime(new Date()), []);
  const rating = ui.net.value.rating?.rating ?? 1000;

  return (
    <Shell
      screen="main_menu"
      title="CHANNEL GUIDE"
      kicker={time}
      class="hf-guide"
      onBack={() => actions.goto('title')}
      hints={[
        ['confirm', 'SELECT'],
        ['back', 'BACK'],
        ['up|down', 'CHANNEL'],
      ]}
      ticker={TICKER}
    >
      {!progress.value.offered && newHereEligible.value ? <NewHereOffer /> : null}
      <nav class="hf-guide__list" aria-label="Channels">
        {CHANNELS.map((c, i) => (
          <button class="hf-guide__row interactive" key={c.id} {...grid.item(i)} data-channel={c.id}>
            <span class="hf-guide__ch">
              <small>CH</small>
              <b>{String(i + 1).padStart(2, '0')}</b>
            </span>
            <span class="hf-guide__show">
              <span class="hf-guide__title t-display">{c.title}</span>
              <span class="hf-guide__desc">{c.desc}</span>
            </span>
            <span class="hf-guide__tag t-broadcast">{c.id === 'tutorial' ? tutorialTag() : c.tag}</span>
          </button>
        ))}
      </nav>

      <aside class="hf-guide__side" aria-live="polite">
        <div class="hf-monitor" key={ch.id}>
          <div class={`hf-monitor__screen scene-${ch.scene ?? 'vs'}`}>
            <Rays a={ch.rays[0]} b={ch.rays[1]} rays={14} y="60%" speed={50} />
            <MonitorScene ch={ch} />
            <div class={`hf-monitor__mode t-display ${ch.title.length > 8 ? 'long' : ''}`}>{ch.title}</div>
            <div class="hf-monitor__onair t-broadcast">
              <span class="hf-dot on" aria-hidden="true" />
              ON AIR
            </div>
            <div class="hf-monitor__scan" aria-hidden="true" />
          </div>
        </div>
        <div class="hf-guide__info hf-panel">
          <p>{ch.info}</p>
          {ch.id === 'ranked' ? (
            <div class="hf-ladder" role="list">
              {RANK_TIERS.map((t, i) => (
                <span
                  key={t.name}
                  role="listitem"
                  class={`hf-ladder__step t-broadcast ${i === rankTierIndex(rating) ? 'current' : ''} tier-${i}`}
                >
                  {t.name}
                </span>
              ))}
            </div>
          ) : (
            <div class="hf-guide__roster">
              {CHARACTERS.map((c) => (
                <span key={c.id} class="hf-guide__roster-chip t-broadcast" style={{ '--accent': c.accent }}>
                  {c.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </aside>
    </Shell>
  );
}

/** The ON AIR picture of a channel (VS card for the match channels). */
function MonitorScene({ ch }: { ch: Channel }) {
  const scene = ch.scene ?? 'vs';
  if (scene === 'vs')
    return (
      <>
        <div class="hf-monitor__rig left">
          <Head character={ch.heads[0]} expression="determined" class="hf-monitor__head" />
          <Glove side="home" pose="ready" class="hf-monitor__glove" />
        </div>
        <div class="hf-monitor__rig right">
          <Glove side="away" pose="ready" class="hf-monitor__glove" />
          <Head character={ch.heads[1]} expression="determined" flip class="hf-monitor__head" />
        </div>
        <div class="hf-monitor__vs t-display" aria-hidden="true">
          VS
        </div>
      </>
    );
  if (scene === 'coach')
    // Coach Grit at his chalkboard: the Football 101 channel is a class, not a fight.
    return (
      <>
        <div class="hf-monitor__rig left solo">
          <Head character="grandpa" expression="determined" class="hf-monitor__head" />
          <Glove side="home" pose="open" class="hf-monitor__glove" />
        </div>
        <svg class="hf-monitor__board" viewBox="0 0 300 200" aria-hidden="true">
          <rect
            x="6"
            y="6"
            width="288"
            height="188"
            rx="14"
            fill="#7a4a22"
            stroke="#140c1c"
            stroke-width="8"
          />
          <rect x="22" y="22" width="256" height="150" rx="6" fill="#1f5a3a" />
          <g stroke="#f4f0e2" stroke-width="5" stroke-linecap="round" fill="none" opacity="0.92">
            <path d="M60 60l22 22M82 60L60 82" />
            <path d="M200 120l22 22M222 120l-22 22" />
            <circle cx="130" cy="128" r="13" />
            <circle cx="214" cy="68" r="13" />
            <path d="M84 94c30 40 70 44 110 34" stroke-dasharray="10 9" />
            <path d="M186 120l10 8-12 4" />
            <path d="M232 168v-10M62 168v-10M150 168v-10" opacity="0.5" />
          </g>
          <rect x="120" y="176" width="70" height="8" rx="4" fill="#f4f0e2" opacity="0.7" />
        </svg>
      </>
    );
  if (scene === 'rules')
    // The counter square, the one diagram every player needs.
    return (
      <div class="hf-monitor__square" aria-hidden="true">
        {(
          [
            ['HURDLE', 'DIVE', 'lime'],
            ['TRUCK', 'LEAP', 'fire'],
            ['DIVE', 'TRUCK', 'cyan'],
            ['LEAP', 'HURDLE', 'pink'],
          ] as const
        ).map(([a, b, tone]) => (
          <span class={`hf-monitor__tile ${tone}`} key={a}>
            <b class="t-display">{a}</b>
            <small class="t-broadcast">BEATS {b}</small>
          </span>
        ))}
      </div>
    );
  if (scene === 'drill')
    // Practice: one head, a row of cones and the infinite-downs badge.
    return (
      <>
        <div class="hf-monitor__rig left solo">
          <Head character={ch.heads[0]} expression="determined" class="hf-monitor__head" />
          <Glove side="home" pose="ready" class="hf-monitor__glove" />
        </div>
        <div class="hf-monitor__cones" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <Art
              keys={['prop/pylon']}
              class="hf-monitor__cone"
              key={i}
              fallback={<i class="hf-monitor__conefb" />}
            />
          ))}
        </div>
        <div class="hf-monitor__badge" aria-hidden="true">
          <svg viewBox="0 0 64 32">
            <path
              d="M16 6c-6 0-10 4.5-10 10s4 10 10 10c9 0 15-20 32-20 6 0 10 4.5 10 10s-4 10-10 10C31 26 25 6 16 6z"
              fill="none"
              stroke="currentColor"
              stroke-width="6"
              stroke-linecap="round"
            />
          </svg>
          <small class="t-broadcast">DOWNS</small>
        </div>
      </>
    );
  // settings: the mixing desk.
  return (
    <div class="hf-monitor__desk" aria-hidden="true">
      <svg class="hf-monitor__gear" viewBox="0 0 100 100">
        <path
          d="M43 4h14l2 12 9 4 10-7 10 10-7 10 4 9 12 2v14l-12 2-4 9 7 10-10 10-10-7-9 4-2 12H43l-2-12-9-4-10 7-10-10 7-10-4-9-12-2V43l12-2 4-9-7-10 10-10 10 7 9-4z"
          fill="#ffd23f"
          stroke="#140c1c"
          stroke-width="5"
          stroke-linejoin="round"
        />
        <circle cx="50" cy="50" r="15" fill="#2c1760" stroke="#140c1c" stroke-width="5" />
      </svg>
      <div class="hf-monitor__sliders">
        {[78, 52, 90].map((v, i) => (
          <span class="hf-monitor__slider" key={i} style={{ '--v': `${v}%`, '--i': i }}>
            <i />
          </span>
        ))}
      </div>
    </div>
  );
}
